import { useNavigate, useSearch } from '@tanstack/react-router';
import { z } from 'zod';
import type { ScopeState } from '../components/filters/types';
import { DEFAULT_KPI } from '../components/media/data/constants';
import { scopeFromSearch, scopeSearchShape, serializeScope } from './scope-search';

export const REPORTS_ROUTE = '/reports/performance';
/** Campaign names belong to these keys' options; a change here leaves the old picks unselectable. */
const CAMPAIGN_SCOPE_KEYS = ['brand', 'platform', 'account', 'range'] as const;

export const reportsSearchSchema = z
  .object({
    ...scopeSearchShape,
    kpi: z.string().optional().catch(undefined),
    campaign: z.array(z.string()).optional().catch(undefined),
  })
  .catch({});

export type ReportsSearch = z.infer<typeof reportsSearchSchema>;
export interface ReportsSearchChange {
  scope?: ScopeState;
  kpi?: string;
  campaign?: string[];
}

export function validateReportsSearch(search: Record<string, unknown>): ReportsSearch {
  const parsed = reportsSearchSchema.parse(search);
  const result: ReportsSearch = {};
  if (parsed.brand !== undefined) result.brand = parsed.brand;
  if (parsed.platform !== undefined) result.platform = parsed.platform;
  if (parsed.account !== undefined) result.account = parsed.account;
  if (parsed.creativeType !== undefined) result.creativeType = parsed.creativeType;
  if (parsed.range !== undefined) result.range = parsed.range;
  if (parsed.kpi !== undefined) result.kpi = parsed.kpi;
  if (parsed.campaign !== undefined && parsed.campaign.length > 0)
    result.campaign = parsed.campaign;
  return result;
}

function stableKey(value: unknown): string {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    return JSON.stringify(value) ?? 'undefined';
  }
  const entries = Object.entries(value).filter(([, entry]) => entry !== undefined);
  return JSON.stringify(entries.sort(([a], [b]) => a.localeCompare(b)));
}

export function mergeReportsSearch(
  previous: Record<string, unknown>,
  next: ReportsSearchChange,
): Record<string, unknown> {
  const merged: Record<string, unknown> = { ...previous };
  if (next.scope) {
    const serialized = serializeScope(next.scope);
    for (const key of Object.keys(scopeSearchShape)) {
      const value = serialized?.[key as keyof typeof scopeSearchShape];
      if (value !== undefined) merged[key] = value;
      else delete merged[key];
    }
    const moved = CAMPAIGN_SCOPE_KEYS.some(
      (key) => stableKey(previous[key]) !== stableKey(merged[key]),
    );
    if (moved) delete merged['campaign'];
  }
  if (next.kpi !== undefined) merged['kpi'] = next.kpi;
  if (next.campaign !== undefined) {
    if (next.campaign.length > 0) merged['campaign'] = next.campaign;
    else delete merged['campaign'];
  }
  return merged;
}

export function useReportsSearch() {
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as ReportsSearch;

  const apply = (next: ReportsSearchChange): void => {
    void navigate({
      to: REPORTS_ROUTE,
      replace: true,
      search: (previous) => mergeReportsSearch(previous as Record<string, unknown>, next),
    });
  };

  return {
    scope: scopeFromSearch(search),
    hasScope: [search.brand, search.platform, search.account].some(
      (value) => value !== undefined && value.length > 0,
    ),
    kpi: search.kpi ?? DEFAULT_KPI,
    campaign: search.campaign ?? [],
    setScope: (scope: ScopeState) => apply({ scope }),
    setKpi: (kpi: string) => apply({ kpi }),
    setCampaign: (campaign: string[]) => apply({ campaign }),
  };
}
